import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { monthnames } from "../../../enums";
import "./dateChoice.css";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import mut from "../hooks/mut";

/**
 * A ultra-modern date picker component with a glassmorphic calendar interface.
 * @component
 * @param {Object} props
 * @param {string} [props.value] - The selected date value (YYYY-MM-DD)
 * @param {Function} props.setValue - Callback function to update the selected date
 * @param {number[]} [props.enabledDays] - Array of enabled days (0-6)
 * @param {boolean} [props.noFuture] - Disable future dates
 * @param {boolean} [props.noPast] - Disable past dates
 * @param {number} [props.width=300] - Calendar width
 * @param {string} [props.label=""] - Input label
 * @param {string} [props.align="left"] - Text alignment
 * @param {string} [props.color="#0284c7"] - Accent color (default Sky-600)
 * @param {HTMLElement} [props.portalContainer] - Optional container for calendar portal (defaults to document.body)
 * @returns {JSX.Element} Modern Date picker
 */
export default function DateChoice(props) {
  const {
    value,
    setValue,
    enabledDays,
    noFuture,
    noPast,
    width = 300,
    label = "",
    fullWidth = true,
    url,
    field,
    align = "left",
    color = "#0284c7",
    style = {},
    error = false,
    className = "",
    noTextBox = false,
    portalContainer = null,
  } = props;

  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());
  const [open, setOpen] = useState(false);
  const [calendarAlign, setCalendarAlign] = useState("left");
  const calendarRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (open && triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const calendarHeight = 350;
      const calendarWidth = Math.max(width || 300, 300);
      
      let top, left;
      
      if (portalContainer) {
        // Absolute positioning relative to the portal container
        const containerRect = portalContainer.getBoundingClientRect();
        
        // Position below trigger, relative to container
        top = triggerRect.bottom - containerRect.top + 12;
        left = triggerRect.left - containerRect.left;
        
        // Adjust for container scroll
        top += portalContainer.scrollTop || 0;
        
        // Flip above if no room below (within viewport)
        if (top + calendarHeight > window.innerHeight - containerRect.top) {
          top = triggerRect.top - containerRect.top - calendarHeight - 12;
        }
        
        // Align right edge if overflowing container
        if (left + calendarWidth > containerRect.width) {
          left = Math.max(0, triggerRect.right - containerRect.left - calendarWidth);
        }
      } else {
        // Fixed positioning relative to viewport (original behavior)
        top = triggerRect.bottom + 12;
        left = triggerRect.left;
        
        // Flip above if no room below
        if (top + calendarHeight > window.innerHeight) {
          top = triggerRect.top - calendarHeight - 12;
        }
        // Align right edge if overflowing right
        if (left + calendarWidth > window.innerWidth) {
          left = Math.max(0, triggerRect.right - calendarWidth);
        }
      }
      
      setPosition({ top, left });
    }
  }, [open, width, portalContainer]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        triggerRef.current && !triggerRef.current.contains(event.target) &&
        calendarRef.current && !calendarRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const set = (val) => {
    setValue(val);
    setOpen(false);
    if (url && field) {
      mut("post", url, { [field]: val });
    }
  };

  const toggleCalendar = (e) => {
    e.stopPropagation();
    setOpen(!open);
  };

  const displayDate = value ? new Date(value).toLocaleDateString(undefined, { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  }) : "";

    return (
      <div className={`premium-date-choice-root ${fullWidth ? 'w-full' : ''}`} style={style}>
        {label && <label className="premium-date-label">{label}</label>}
        
        {noTextBox ? (
          <div 
            ref={triggerRef}
            className="premium-date-simple-trigger" 
            onClick={toggleCalendar}
          >
            {value ? new Date(value).fDate() : "Select Date"}
          </div>
        ) : (
          <div 
            ref={triggerRef}
            className={`premium-date-input-wrapper ${open ? 'active' : ''} ${error ? 'error' : ''} ${className}`}
            onClick={toggleCalendar}
          >
            <div className="premium-date-display" style={{ textAlign: align }}>
              {displayDate || <span className="placeholder">Pick a date...</span>}
            </div>
            <div className="premium-date-icon">
              <CalendarIcon size={18} strokeWidth={2.5} />
            </div>
          </div>
        )}

        {open && createPortal(
          <div 
            ref={calendarRef}
            className={`premium-calendar-positioner ${portalContainer ? 'absolute' : ''}`}
            style={{ 
              top: `${position.top}px`, 
              left: `${position.left}px` 
            }}
          >
            <Calendar
              year={year}
              month={month}
              set={set}
              open={open}
              setYear={setYear}
              setMonth={setMonth}
              value={value}
              enabledDays={enabledDays}
              noFuture={noFuture}
              noPast={noPast}
              width={width}
              color={color}
            />
          </div>,
          portalContainer || document.body
        )}
      </div>
    );
}

function Calendar(props) {
  const { year, month, set, open, setYear, setMonth, value, enabledDays, noFuture, noPast, width, color } = props;
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const days = getDates(year, month, enabledDays, noFuture, noPast);

  const changeMonth = (e, offset) => {
    e.stopPropagation();
    let newMonth = month + offset;
    let newYear = year;
    if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    }
    setMonth(newMonth);
    setYear(newYear);
  };

  return (
    <div className="premium-calendar-container" style={{ width: Math.max(width, 300) }}>
      <div className="premium-calendar-header">
        <button className="premium-nav-btn" onClick={(e) => changeMonth(e, -1)}>
          <ChevronLeft size={18} />
        </button>
        <div className="premium-current-month">
          {monthnames[month + 1][0]} <span className="year-accent">{year}</span>
        </div>
        <button className="premium-nav-btn" onClick={(e) => changeMonth(e, 1)}>
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="premium-calendar-weekdays">
        {dayNames.map((wd) => (
          <div key={wd} className="weekday-name">{wd}</div>
        ))}
      </div>

      <div className="premium-calendar-grid">
        {days.map((d, i) => (
          <Day
            key={`day-${i}`}
            day={d}
            set={set}
            selected={d.text === value}
            disabled={d.disabled}
            color={color}
          />
        ))}
      </div>
    </div>
  );
}

function Day({ day, disabled, set, selected, color }) {
  return (
    <div
      className={`premium-day-cell 
        ${disabled ? "disabled" : ""} 
        ${day.differentMonth ? "different-month" : ""} 
        ${selected ? "selected" : ""}
      `}
      onClick={(e) => {
        e.stopPropagation();
        if(!disabled) set(day.text);
      }}
      style={selected ? { "--accent-color": color } : {}}
    >
      <span className="day-number">{day.number}</span>
      {selected && <div className="selection-pulse" />}
    </div>
  );
}

function getDates(year, month, enabledDays, noFuture, noPast) {
  let firstMonday = getPreviousMonday(new Date(year, month, 1));
  let days = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 0; i < 42; i++) {
    let disabled = false;
    const current = new Date(firstMonday);
    current.setHours(0, 0, 0, 0);

    // Get current date string for comparison
    const dateStr = firstMonday.tDate ? firstMonday.tDate() : firstMonday.toISOString().split('T')[0];

    if (enabledDays && !enabledDays.includes(current.getDay())) disabled = true;
    if (noFuture && current > today) disabled = true;
    if (noPast && current < today) disabled = true;

    days.push({
      number: firstMonday.getDate(),
      text: dateStr,
      date: new Date(firstMonday),
      differentMonth: firstMonday.getMonth() !== month,
      disabled,
    });

    firstMonday.setDate(firstMonday.getDate() + 1);
  }
  return days;
}

function getPreviousMonday(date) {
  let currentDate = new Date(date);
  let dayOfWeek = currentDate.getDay();
  let difference = (dayOfWeek === 0) ? 6 : dayOfWeek - 1;
  currentDate.setDate(currentDate.getDate() - difference);
  return currentDate;
}
